import { useEffect, useState } from 'react'

import axios from 'axios'

import queryClient from 'Utils/queryClient'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

export const DESIGNER_OPTIONS_ENDPOINT = '/api/internal/designers'
export const DESIGNER_OPTIONS_QUERY_KEY = ['searchPage', 'designerOptions']
// Api::Internal::DesignersController reads a week-long server-side cache, so a long client window
// costs nothing in freshness and keeps the desktop dropdown and the mobile drawer on one request.
const STALE_TIME = 30 * 60 * 1000

// The endpoint answers in snake_case (bare JSON array, no serializer envelope); the rest of the
// SearchPage store and components speak camelCase. Shared with useDesignerLookup, whose endpoint
// returns the same row shape.
export const normalizeDesigner = (designer) => ({
  username: designer.username,
  modelCount: designer.model_count ?? null,
  ratingPercent: designer.rating_percent ?? null,
  topDesigner: designer.top_designer === true,
  following: designer.following === true,
})

export const fetchDesignerOptions = async () => {
  const { data } = await axios.get(DESIGNER_OPTIONS_ENDPOINT)
  return (Array.isArray(data) ? data : [])
    .filter(designer => designer?.username)
    .map(normalizeDesigner)
}

/**
 * Loads the designer filter options (top 10 designers plus up to 100 designers the viewer follows).
 * There is no server-side pagination or search: the whole list arrives in one shot and the in-list
 * search, scope chips and "show more" cut are all applied client-side.
 *
 * Uses the shared react-query client imperatively (like Store/actions/results.js) because
 * SearchPage is not wrapped in a QueryClientProvider.
 *
 * @returns {{ designers: Array, isLoading: boolean }}
 */
export default function useDesignerOptions() {
  const [state, setState] = useState({ designers: EMPTY_ARRAY, isLoading: true })

  useEffect(() => {
    let isActive = true

    queryClient
      .fetchQuery({
        queryKey: DESIGNER_OPTIONS_QUERY_KEY,
        queryFn: fetchDesignerOptions,
        staleTime: STALE_TIME,
        retry: false,
      })
      // The endpoint degrades to `[]` rather than erroring (a Redis outage returns 200 with an
      // empty body), so a rejection here is a transport failure - fall back to an empty list
      // instead of leaving the dropdown stuck on the loading line.
      .catch(() => EMPTY_ARRAY)
      .then((designers) => {
        if (isActive) {
          setState({ designers, isLoading: false })
        }
      })

    return () => {
      isActive = false
    }
  }, [])

  return state
}
